import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

// ═══════════════════════════════════════════════════════════
//  PROMPT ARCHITECTURE V2 — perkhidmatan client (makmal /pipeline-test, admin sahaja)
//
//  Semua kerja (pustaka prompt, compiler, OpenAI, sejarah ujian) berlaku di SERVER melalui
//  SATU callable `promptArchV2` (functions/promptArchV2.js). Client TIDAK membaca atau menulis
//  Firestore terus untuk ciri ini, dan tiada kunci di sini.
//
//  ⚠️ KEMBAR bentuk payload: `bersihPilihan` / `bersihProduk` (functions/promptArchV2Teras.js)
//     dan pembaca `data?.…` dalam functions/promptArchV2.js. Medan disenarai SATU PERSATU di
//     bawah — medan yang tidak disebut di sini TIDAK akan sampai ke server.
// ═══════════════════════════════════════════════════════════

async function panggil(aksi, medan, timeout = 60000) {
  const call = httpsCallable(functions, 'promptArchV2', { timeout });
  const res = await call({ aksi, ...medan });
  if (!res || res.data == null) throw new Error('Server memulangkan jawapan kosong.');
  return res.data;
}

/** Pilihan Playground → payload. */
export function binaPilihan(t) {
  return {
    kategoriId: t?.kategoriId || '',
    ceritaId: t?.presetId ? '' : (t?.ceritaId || ''),
    gayaId: t?.presetId ? '' : (t?.gayaId || ''),
    presetId: t?.presetId || '',
    adapterId: t?.adapterId || '',
    bilBabak: Number(t?.bilBabak),
    tempoh: Number(t?.tempoh),
  };
}

/**
 * Produk → payload. `sumber` = 'pustaka' (produk yang dipilih melalui picker makmal sedia ada)
 * atau 'manual'. `tambahan` = medan yang admin taip sendiri (dipakai dalam kedua-dua mod).
 */
export function binaProduk({ sumber, pustaka, tambahan }) {
  const t = tambahan || {};
  const intel = pustaka?.intelligenceData || {};
  const dariPustaka = sumber !== 'manual';
  return {
    sumber: dariPustaka ? 'pustaka' : 'manual',
    idPustaka: dariPustaka ? (pustaka?._id || '') : '',
    nama: dariPustaka ? (pustaka?.name || '') : (t.nama || ''),
    harga: dariPustaka ? (pustaka?.price || '') : (t.harga || ''),
    hargaAsal: dariPustaka ? (pustaka?.originalPrice || '') : '',
    diskaun: dariPustaka ? (pustaka?.discount || '') : '',
    analisisImej: dariPustaka ? (intel.imageAnalysis || '') : '',
    kajianWeb: dariPustaka ? (intel.webResearch || '') : '',
    // Sama seperti tab "Ujian Master Prompt": data scrape mentah hanya dipakai bila tiada kajian web.
    dataScrape: dariPustaka && !intel.webResearch ? (pustaka?.scrapedData || null) : null,
    penerangan: t.penerangan || '',
    subkategori: t.subkategori || '',
    ciriJualan: t.ciriJualan || '',
    spesifikasi: t.spesifikasi || '',
    sasaran: t.sasaran || '',
    nota: t.nota || '',
  };
}

/**
 * Vision: gambar produk library → { url } untuk server, atau { ralat } yang boleh dipapar.
 * Semakan ringan di sini (supaya butang Generate dikunci dengan sebab); server = hakim terakhir
 * (`bersihGambar` dalam functions/promptArchV2Teras.js — ⚠️ KEMBAR peraturan https/data:image).
 */
export function gambarProduk(pustaka) {
  const url = String(pustaka?.imageBase64 || pustaka?.imageUrl || '').trim();
  if (!url) return { ralat: 'Produk ini tiada gambar (atau gambar belum dimuat — pilih semula produk dari library).' };
  if (url.startsWith('data:')) {
    return /^data:image\/(png|jpe?g|webp|gif);base64,/.test(url) ? { url } : { ralat: 'Data gambar produk ini bukan PNG/JPEG/WEBP/GIF.' };
  }
  return /^https:\/\/\S+$/.test(url) ? { url } : { ralat: 'Link gambar produk ini tidak sah (mesti https://).' };
}

/** Borang entri pustaka → data untuk disimpan. Medan diambil daripada takrif pustaka (dari server). */
export function binaDataEntri(def, borang) {
  const data = {
    nama: borang?.nama ?? '',
    penerangan: borang?.penerangan ?? '',
    aktif: borang?.aktif !== false,
    diarkib: borang?.diarkib === true,
  };
  for (const m of def?.medan || []) data[m.k] = borang?.[m.k] ?? (m.jenis === 'rujukBanyak' ? [] : '');
  return data;
}

/** Entri pustaka → nilai borang (senarai jadi teks berbaris, nombor jadi teks). */
export function borangDari(def, entri) {
  const b = {
    nama: entri?.nama || '',
    penerangan: entri?.penerangan || '',
    aktif: entri ? entri.aktif !== false : true,
    diarkib: entri?.diarkib === true,
  };
  for (const m of def?.medan || []) {
    const v = entri?.[m.k];
    if (m.jenis === 'senarai') b[m.k] = Array.isArray(v) ? v.join('\n') : '';
    else if (m.jenis === 'senaraiNombor') b[m.k] = Array.isArray(v) ? v.join(', ') : '';
    else if (m.jenis === 'rujukBanyak') b[m.k] = Array.isArray(v) ? [...v] : [];
    else if (m.jenis === 'nombor') b[m.k] = v == null ? '' : String(v);
    else b[m.k] = v == null ? '' : String(v);
  }
  return b;
}

export const muatArchV2 = () => panggil('muat', {});

export const simpanEntri = ({ jenis, id, data, versiAsas, baharu }) =>
  panggil('simpan', { jenis, id: id || '', data, versiAsas: versiAsas ?? null, baharu: baharu === true });

export const senaraiVersi = ({ jenis, id }) => panggil('versi', { jenis, id });

export const pulihVersi = ({ jenis, id, versi, versiAsas }) => panggil('pulih', { jenis, id, versi, versiAsas });

// Inspector hanya perlu TAHU vision hidup (untuk peraturan dalam prompt) — gambar tidak dihantar.
export const kompilPrompt = ({ pilihan, produk, vision }) => panggil('kompil', { pilihan, produk, vision: vision === true });

// Timeout client 320s > timeout function 300s, supaya ralat sebenar server yang sampai dahulu.
export const janaUjian = ({ pilihan, produk, model, reasoning, verbosity, maxTokens, kumpulan, vision, gambarUrl }) =>
  panggil('jana', {
    pilihan, produk, model, reasoning, verbosity: verbosity || '', maxTokens: Number(maxTokens), kumpulan: kumpulan || '',
    vision: vision === true, gambar: vision === true ? { url: gambarUrl || '' } : null,
  }, 320000);

export const senaraiSejarah = (had = 40) => panggil('sejarah', { had });

export const bacaRun = (id) => panggil('run', { id });

export const simpanNilai = ({ id, nilai, catatan }) => panggil('nilai', { id, nilai, catatan: catatan || '' });

/** Auto-detect kategori (Workspace pilot). `produk` = [{ kunci, nama, ringkas }] — satu panggilan untuk semua. */
export const kesanKategori = (produk) =>
  panggil('kategori', { produk: (produk || []).map((p) => ({ kunci: p.kunci, nama: p.nama, ringkas: p.ringkas || '' })) }, 90000);

// ── pembantu paparan (dikongsi komponen ArchV2*) ──
export const usd = (x) => (x == null ? 'tiada harga' : '$' + Number(x).toFixed(4));
/** Unjuran kos: kos satu call × bilangan call sehari (dan × 30 hari). Anggaran — harga semasa makmal. */
export const CALL_SEHARI = 2000;
export const unjuranKos = (kosSatu) => (kosSatu == null ? null : { sehari: kosSatu * CALL_SEHARI, sebulan: kosSatu * CALL_SEHARI * 30 });
export const usd2 = (x) => (x == null ? '—' : '$' + Number(x).toFixed(2));
export const saat = (msLat) => (msLat == null ? '—' : (Number(msLat) / 1000).toFixed(1) + 's');
export const masa = (msEpoch) => (msEpoch
  ? new Date(msEpoch).toLocaleString('ms-MY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—');
export const sebagaiTeks = (v) => (typeof v === 'string' ? v : (v == null ? '' : JSON.stringify(v)));

export const STATUS_RUN = {
  ok: { teks: 'OK', kelas: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  tidak_sah: { teks: 'TIDAK SAH', kelas: 'bg-rose-50 text-rose-700 border-rose-200' },
  terpotong: { teks: 'TERPOTONG', kelas: 'bg-amber-50 text-amber-800 border-amber-200' },
  gagal: { teks: 'GAGAL', kelas: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export async function salin(teks) {
  try {
    await navigator.clipboard.writeText(String(teks ?? ''));
    return true;
  } catch {
    return false;
  }
}
